/**
 * Service Providers Page.
 *
 * Full CRUD for external maintenance service providers with contacts.
 * Follows the VendorsPage pattern. Embedded in the ShopHub.
 */

import { useState } from "react";
import {
  Plus,
  Building2,
  Phone,
  Mail,
  Globe,
  Power,
  PowerOff,
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import {
  useServiceProviders,
  useCreateServiceProvider,
  useDeleteServiceProvider,
} from "@/features/maintenance/api";
import type { ServiceProvider } from "@/features/maintenance/api";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import ActionDrawer from "@/components/ui/ActionDrawer";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

function ServiceProvidersPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useServiceProviders();
  const createMutation = useCreateServiceProvider();
  const deleteMutation = useDeleteServiceProvider();

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [specializations, setSpecializations] = useState("");
  const [notes, setNotes] = useState("");

  const resetForm = () => {
    setName("");
    setContactName("");
    setEmail("");
    setPhone("");
    setWebsite("");
    setPaymentTerms("");
    setHourlyRate("");
    setSpecializations("");
    setNotes("");
  };

  const reactivateMutation = useMutation({
    mutationFn: async (id: string) =>
      (
        await apiClient.put(`/api/v1/service-providers/${id}`, {
          is_active: true,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service-providers"] });
      toast.success("Service provider reactivated");
    },
  });

  const providers = data?.items || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Service Providers
          </h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Manage external shops, contractors, and service partners.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" /> Add Provider
        </Button>
      </div>

      {/* Create Drawer */}
      <ActionDrawer
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Service Provider"
        description="Add an external service provider to your organization."
        icon={Building2}
        size="md"
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await createMutation.mutateAsync({
              name,
              contact_name: contactName || undefined,
              email: email || undefined,
              phone: phone || undefined,
              website: website || undefined,
              payment_terms: paymentTerms || undefined,
              hourly_rate: hourlyRate ? parseFloat(hourlyRate) : undefined,
              specializations: specializations
                ? specializations
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)
                : undefined,
              notes: notes || undefined,
            });
            setShowCreate(false);
            resetForm();
            toast.success("Service provider created");
          }}
          className="space-y-4"
        >
          <Input
            label="Business Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="ABC Repair Shop"
          />
          <Input
            label="Primary Contact"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="John Smith"
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="shop@example.com"
            />
            <Input
              label="Phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="555-0100"
            />
          </div>
          <Input
            label="Website"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="https://..."
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Payment Terms"
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              placeholder="Net 30"
            />
            <Input
              label="Hourly Rate ($)"
              type="number"
              step="0.01"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(e.target.value)}
              placeholder="85.00"
            />
          </div>
          <Input
            label="Specializations (comma-separated)"
            value={specializations}
            onChange={(e) => setSpecializations(e.target.value)}
            placeholder="engine, transmission, body, electrical"
          />
          <div>
            <label className="block text-[13px] font-medium text-[var(--text-primary)]">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Additional notes..."
              className="mt-1.5 block w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowCreate(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              isLoading={createMutation.isPending}
            >
              Create Provider
            </Button>
          </div>
        </form>
      </ActionDrawer>

      {/* List */}
      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : providers.length > 0 ? (
        <div className="space-y-3 stagger-children">
          {providers.map((p: ServiceProvider) => (
            <Card key={p.id} className="hover-lift">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
                  <Building2 className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-sm font-semibold text-[var(--text-primary)]">
                      {p.name}
                    </h3>
                    {!p.is_active && <Badge variant="danger">Inactive</Badge>}
                    {p.payment_terms && (
                      <Badge variant="default">{p.payment_terms}</Badge>
                    )}
                    {p.hourly_rate && (
                      <Badge variant="info">${Number(p.hourly_rate)}/hr</Badge>
                    )}
                  </div>
                  {p.contact_name && (
                    <p className="text-xs text-[var(--text-secondary)] mt-0.5">
                      {p.contact_name}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-[var(--text-tertiary)]">
                    {p.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {p.email}
                      </span>
                    )}
                    {p.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {p.phone}
                      </span>
                    )}
                    {p.website && (
                      <span className="flex items-center gap-1">
                        <Globe className="h-3 w-3" />
                        {p.website}
                      </span>
                    )}
                  </div>
                  {p.specializations && p.specializations.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {(p.specializations as string[]).map((s) => (
                        <span
                          key={s}
                          className="inline-flex items-center rounded-full bg-[var(--surface-sunken)] px-2 py-0.5 text-[10px] font-medium text-[var(--text-secondary)] capitalize"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {p.is_active ? (
                    <button
                      onClick={() => deleteMutation.mutate(p.id)}
                      title="Deactivate"
                      className="p-1.5 text-[var(--text-tertiary)] hover:text-[var(--text-warning)]"
                    >
                      <PowerOff className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <button
                      onClick={() => reactivateMutation.mutate(p.id)}
                      title="Reactivate"
                      className="p-1.5 text-[var(--text-tertiary)] hover:text-[var(--text-success)]"
                    >
                      <Power className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <Building2 />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No service providers
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Add external shops and contractors that perform maintenance work.
            </p>
            <Button className="mt-4" onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4" /> Add Provider
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

export default ServiceProvidersPage;
